import React from 'react';
import { Box } from '@mui/material';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import ReactMarkdown from 'react-markdown';

// One look for every place the chat appears (corner, large dialog, support page): the
// assistant in a light grey bubble behind the same light-blue avatar the support page uses
// for its section icons, the user in the accent blue of the app's buttons.
export const CHAT_COLORS = {
  userBubble: '#3182ce',
  assistantBubble: '#f1f5f9',
  avatarBg: '#ebf4ff',
  avatarIcon: '#3182ce',
  muted: '#94a3b8',
};

function TypingIndicator() {
  return (
    <Box sx={{ display: 'flex', gap: '4px', alignItems: 'center', py: 0.5 }}>
      {[0, 1, 2].map((i) => (
        <Box
          key={i}
          sx={{
            width: 7,
            height: 7,
            borderRadius: '50%',
            bgcolor: CHAT_COLORS.muted,
            animation: 'chatbotBounce 1.2s infinite',
            animationDelay: `${i * 0.2}s`,
            '@keyframes chatbotBounce': {
              '0%, 60%, 100%': { transform: 'translateY(0)' },
              '30%': { transform: 'translateY(-4px)' },
            },
          }}
        />
      ))}
    </Box>
  );
}

export function AssistantAvatar({ size = 28 }) {
  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: '50%',
        bgcolor: CHAT_COLORS.avatarBg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <SmartToyOutlinedIcon sx={{ fontSize: size * 0.6, color: CHAT_COLORS.avatarIcon }} />
    </Box>
  );
}

function ChatMessage({ role, content, isLoading }) {
  const isUser = role === 'user';

  if (isUser) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
        <Box
          sx={{
            maxWidth: '85%',
            bgcolor: CHAT_COLORS.userBubble,
            color: '#fff',
            px: 2,
            py: 1.25,
            borderRadius: '16px 16px 4px 16px',
            fontSize: '0.875rem',
            lineHeight: 1.5,
            wordBreak: 'break-word',
          }}
        >
          <Box sx={{ color: 'inherit', whiteSpace: 'pre-wrap' }}>
            {content}
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', gap: 1, mb: 1.5, alignItems: 'flex-start' }}>
      <Box sx={{ mt: 0.5 }}>
        <AssistantAvatar />
      </Box>
      <Box
        sx={{
          maxWidth: '85%',
          bgcolor: CHAT_COLORS.assistantBubble,
          color: 'text.primary',
          px: 2,
          py: 1.25,
          borderRadius: '16px 16px 16px 4px',
          fontSize: '0.875rem',
          lineHeight: 1.5,
          wordBreak: 'break-word',
          // Markdown content styling
          '& p': { m: 0, mb: 1 },
          '& p:last-child': { mb: 0 },
          '& ul, & ol': { m: 0, pl: 2.5, mb: 1 },
          '& ul:last-child, & ol:last-child': { mb: 0 },
          '& li': { mb: 0.25 },
          '& strong': { fontWeight: 600 },
          '& code': {
            bgcolor: '#fff',
            px: 0.5,
            py: 0.25,
            borderRadius: 0.5,
            fontSize: '0.8em',
          },
          '& a': { color: CHAT_COLORS.userBubble },
          '& h1, & h2, & h3, & h4': {
            fontSize: '0.9rem',
            fontWeight: 600,
            mt: 1,
            mb: 0.5,
          },
          '& h1:first-of-type, & h2:first-of-type, & h3:first-of-type': { mt: 0 },
        }}
      >
        {isLoading ? (
          <TypingIndicator />
        ) : (
          <ReactMarkdown>{content || ''}</ReactMarkdown>
        )}
      </Box>
    </Box>
  );
}

export default ChatMessage;
